import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ReportModal.css';
import {
  popoverContainerStyle,
  popoverLabelStyle,
  popoverSelectStyle,
  popoverTextareaStyle,
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './PopoverStyles';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared Report Modal Component
 * Used for reporting posts, marketplace objects, and comments
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Close/cancel callback
 * @param {Function} onSubmit - Submit callback (category, reason) => Promise
 * @param {string} type - Type of content being reported: 'post' | 'marketplace' | 'comment'
 * @param {boolean} isSubmitting - Whether submission is in progress
 */
const ReportModal = ({
  isOpen,
  onClose,
  onSubmit,
  type = 'post',
  isSubmitting = false
}) => {
  const { t } = useLanguage();
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const modalRef = useRef(null);
  const selectRef = useRef(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCategory('');
      setReason('');
      // Focus the select element when modal opens
      setTimeout(() => selectRef.current?.focus(), 10);
    }
  }, [isOpen]);

  // Handle keyboard navigation and focus trap
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape' && !isSubmitting) {
      e.preventDefault();
      onClose();
    }

    // Focus trap
    if (e.key === 'Tab') {
      const focusableElements = modalRef.current?.querySelectorAll(
        'select, textarea, button:not([disabled])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, isSubmitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleSubmit = () => {
    if (!category) {
      alert(t('report.selectCategoryAlert') || 'Bitte wähle eine Kategorie aus');
      return;
    }
    onSubmit(category, reason || null);
  };

  const getTitle = () => {
    switch (type) {
      case 'marketplace':
        return t('marketplace.reportObject');
      case 'comment':
        return t('comments.reportComment');
      default:
        return t('posts.reportPost');
    }
  };

  const getWhyReportText = () => {
    switch (type) {
      case 'marketplace':
        return t('report.whyReportObject');
      case 'comment':
        return t('report.whyReportComment');
      default:
        return t('report.whyReport');
    }
  };

  const getCategories = () => {
    if (type === 'marketplace') {
      return [
        { value: 'inappropriate_content', label: t('report.marketplaceCategories.inappropriateContent') },
        { value: 'threatening_content', label: t('report.marketplaceCategories.threateningContent') },
        { value: 'commercial_seller', label: t('report.marketplaceCategories.commercialSeller') },
        { value: 'fraudulent_content', label: t('report.marketplaceCategories.fraudulentContent') },
        { value: 'counterfeit', label: t('report.marketplaceCategories.counterfeit') },
        { value: 'academic_integrity', label: t('report.marketplaceCategories.academicIntegrity') },
      ];
    }
    // Post and comment categories are the same
    return [
      { value: 'hate_speech', label: t('report.categories.hateSpeech') },
      { value: 'spam', label: t('report.categories.spam') },
      { value: 'personal_data', label: t('report.categories.personalData') },
      { value: 'misinformation', label: t('report.categories.misinformation') },
      { value: 'violence', label: t('report.categories.violence') },
      { value: 'sexual', label: t('report.categories.sexual') },
    ];
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={isSubmitting ? undefined : onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={popoverContainerStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
      >
        {/* Header with flag icon */}
        <div className="report-modal-header">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1v12zm0 0v6" stroke="rgba(204, 6, 5, 0.62)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span id="report-modal-title" className="report-modal-title">
            {getTitle()}
          </span>
        </div>

        {/* Category prompt */}
        <p style={popoverLabelStyle}>{t('report.selectCategory')}</p>

        {/* Category dropdown */}
        <select
          ref={selectRef}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{
            ...popoverSelectStyle,
            color: category ? '#000000' : '#878787'
          }}
          aria-label={t('report.selectCategory')}
        >
          <option value="">{t('report.chooseCategory')}</option>
          {getCategories().map((cat) => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>

        {/* Optional reason header */}
        <p style={popoverLabelStyle}>{getWhyReportText()}</p>

        {/* Textarea for optional reason */}
        <textarea
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          placeholder={t('report.reasonPlaceholder')}
          style={{ ...popoverTextareaStyle, overflow: 'hidden', resize: 'none' }}
          aria-label={getWhyReportText()}
        />

        {/* Buttons */}
        <div style={popoverButtonContainerStyle}>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            style={getCancelButtonStyle(isSubmitting)}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !category}
            style={getSubmitButtonStyle(isSubmitting || !category)}
          >
            {isSubmitting ? t('report.submitting') : t('report.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
